import { Controller } from "@hotwired/stimulus"
import lottie from "lottie-web"

export default class extends Controller {
  static values = {
    path: String,
    loop: { type: Boolean, default: true },
    autoplay: { type: Boolean, default: true },
    width: { type: String, default: "100px" },
    height: { type: String, default: "100px" },
    color: String
  }

  connect() {
    // CSS pulsing-dot fallback (`.lottie-animation:not(.lottie-loaded)::before`)
    // shows until the animation actually renders — covering the initial fetch,
    // a slow fetch, and a failed fetch (on failure the class is never added,
    // so the fallback simply persists instead of an empty box).
    this.element.classList.remove('lottie-loaded')
    this.loadAnimation()
    this.isStoppingSmooth = false

    // Listen for custom complete events
    this.handleComplete = this.handleComplete.bind(this)
    this.element.addEventListener('lottie:complete', this.handleComplete)
  }

  disconnect() {
    if (this.animation) {
      this.animation.destroy()
      this.animation = null
    }
    this.element.classList.remove('lottie-loaded')
    this.element.removeEventListener('lottie:complete', this.handleComplete)
  }

  async loadAnimation() {
    try {
      // Fetch the JSON animation file
      const response = await fetch(this.pathValue)
      if (!response.ok) {
        throw new Error(`Failed to load animation: ${response.statusText}`)
      }

      let animationData = await response.json()

      // Apply color replacement if color is provided
      if (this.hasColorValue && this.colorValue) {
        animationData = this.replaceColors(animationData, this.colorValue)
      }

      // Create the Lottie animation - always start with autoplay false
      this.animation = lottie.loadAnimation({
        container: this.element,
        renderer: 'svg',
        loop: false, // Always create without loop initially
        autoplay: false, // Always create without autoplay
        animationData: animationData,
        rendererSettings: {
          preserveAspectRatio: 'xMidYMid slice'
        }
      })

      // Wait for the animation to be ready
      this.animation.addEventListener('DOMLoaded', () => {
        // Animation has rendered — retire the CSS pulsing-dot fallback.
        this.element.classList.add('lottie-loaded')

        if (!this.autoplayValue) {
          // For static display, go to first frame and pause
          this.animation.goToAndStop(0, true)
          this.animation.pause() // Ensure it's paused
        } else {
          // For animated display, set loop and play
          this.animation.loop = this.loopValue
          this.animation.play()
        }

        // Dispatch event when animation is loaded
        this.dispatch("loaded", { detail: { animation: this.animation } })
      })

    } catch (error) {
      this.dispatch("error", { detail: { error: error.message } })
    }
  }

  play() {
    if (this.animation) {
      // Reset stopping flag when starting new animation
      this.isStoppingSmooth = false
      // Ensure loop is set correctly
      this.animation.loop = this.loopValue
      this.animation.play()
    } else {
      console.warn('[LottieController] Cannot play - animation not loaded yet')
    }
  }

  pause() {
    if (this.animation) {
      this.animation.pause()
    }
  }

  stop() {
    if (this.animation) {
      this.animation.stop()
    }
  }

  setSpeed(speed) {
    if (this.animation) {
      this.animation.setSpeed(speed)
    }
  }

  setDirection(direction) {
    if (this.animation) {
      this.animation.setDirection(direction)
    }
  }

  goToAndPlay(frame) {
    if (this.animation) {
      this.animation.goToAndPlay(frame, true)
    }
  }

  goToAndStop(frame) {
    if (this.animation) {
      this.animation.goToAndStop(frame, true)
    }
  }

  // Replace all fill and stroke colors in the animation JSON
  // Converts CSS color (hex/rgb/named) to Lottie's [r, g, b, a] format
  replaceColors(data, cssColor) {
    const rgbaColor = this.cssColorToLottieRGBA(cssColor)

    // Deep clone to avoid mutating original
    const newData = JSON.parse(JSON.stringify(data))

    // Recursively find and replace color properties
    this.replaceColorRecursive(newData, rgbaColor)

    return newData
  }

  // Recursively traverse the animation data and replace colors
  replaceColorRecursive(obj, rgbaColor) {
    if (typeof obj !== 'object' || obj === null) return

    for (const key in obj) {
      if (key === 'c' && obj[key] && typeof obj[key] === 'object') {
        // Found a color property (fill or stroke)
        // Lottie colors are in format: {"a":0,"k":[r,g,b,a],"ix":4}
        if (obj[key].k && Array.isArray(obj[key].k) && obj[key].k.length >= 3) {
          // Replace the color values but preserve alpha if it exists
          const originalAlpha = obj[key].k[3] !== undefined ? obj[key].k[3] : 1
          obj[key].k = [...rgbaColor.slice(0, 3), originalAlpha]
        }
      } else if (typeof obj[key] === 'object') {
        // Recursively process nested objects/arrays
        this.replaceColorRecursive(obj[key], rgbaColor)
      }
    }
  }

  // Convert CSS color to Lottie RGBA format [r, g, b, a]
  // Lottie uses 0-1 range, not 0-255
  cssColorToLottieRGBA(cssColor) {
    // First try to parse hex colors directly for reliability
    const hexMatch = cssColor.match(/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i)
    if (hexMatch) {
      let hex = hexMatch[1]
      // Expand 3-digit hex to 6-digit
      if (hex.length === 3) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2]
      }
      const r = parseInt(hex.slice(0, 2), 16) / 255
      const g = parseInt(hex.slice(2, 4), 16) / 255
      const b = parseInt(hex.slice(4, 6), 16) / 255
      const a = hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1
      return [r, g, b, a]
    }

    // Fall back to using the browser to parse other color formats
    const temp = document.createElement('div')
    temp.style.color = cssColor
    document.body.appendChild(temp)

    const computed = window.getComputedStyle(temp).color
    document.body.removeChild(temp)

    // Parse rgb/rgba format: "rgb(r, g, b)" or "rgba(r, g, b, a)"
    const match = computed.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/)

    if (match) {
      const r = parseInt(match[1]) / 255
      const g = parseInt(match[2]) / 255
      const b = parseInt(match[3]) / 255
      const a = match[4] !== undefined ? parseFloat(match[4]) : 1

      return [r, g, b, a]
    }

    // Modern browsers may return oklch() or color(srgb ...) — use canvas to convert
    try {
      const canvas = document.createElement('canvas')
      canvas.width = canvas.height = 1
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.fillStyle = computed
        ctx.fillRect(0, 0, 1, 1)
        const [cr, cg, cb, ca] = ctx.getImageData(0, 0, 1, 1).data
        return [cr / 255, cg / 255, cb / 255, ca / 255]
      }
    } catch { /* canvas unavailable */ }

    // Fallback to black if parsing fails
    console.warn('Failed to parse color:', cssColor)
    return [0, 0, 0, 1]
  }

  // Handle external complete events
  handleComplete(event) {
    this.smoothStop()
  }

  // Smoothly stop the animation after completing the current loop
  smoothStop() {
    if (!this.animation || this.isStoppingSmooth) return

    this.isStoppingSmooth = true

    // If animation is not playing, just stop
    if (this.animation.isPaused) {
      this.animation.goToAndStop(this.animation.totalFrames - 1, true)
      this.isStoppingSmooth = false
      this.dispatch("stopped", { detail: { animation: this.animation } })
      return
    }

    // Listen for the next loop completion
    const handleLoopComplete = () => {
      // Remove the listener
      this.animation.removeEventListener('loopComplete', handleLoopComplete)

      // Stop at the last frame
      this.animation.loop = false
      this.animation.goToAndStop(this.animation.totalFrames - 1, true)
      this.isStoppingSmooth = false

      // Dispatch stopped event
      this.dispatch("stopped", { detail: { animation: this.animation } })
    }

    // If not looping, listen for complete event instead
    if (!this.animation.loop) {
      const handleComplete = () => {
        this.animation.removeEventListener('complete', handleComplete)
        this.animation.goToAndStop(this.animation.totalFrames - 1, true)
        this.isStoppingSmooth = false
        this.dispatch("stopped", { detail: { animation: this.animation } })
      }
      this.animation.addEventListener('complete', handleComplete)
    } else {
      // For looping animations, stop after current loop
      this.animation.addEventListener('loopComplete', handleLoopComplete)
    }
  }
}
